import { SelectBoxManager } from "./select-box-manager";
import { tryParseString } from "./input-utils";
import { OptionItem, getPrefetchedData } from "./requests";

declare var Turbolinks: any;

declare global {
    interface JQuery {
        collapse(command: string): any;
    }
}

/**
 * Mimic hyper theme initialisation code, but adapt for turbolinks
 * See hyper javascript if more plugins need to be added. Sourcemapped js can be seen in Chrome sources tab
 */
export function initialisePlugins() {
    $(document).on("turbolinks:render", function() {
        setupSidebar();
    });

    $(document).on("turbolinks:load", function() {
        setupSidebar();

        ($('#state-saving-datatable') as any).DataTable({
            stateSave: true,
            "orderClasses": false
        });

        $("[data-plugin='range-slider']").each(function(idx, obj) {
            const $el: any = $(obj);
            $el.ionRangeSlider();
        });

        setupSelect2();

        $("[data-toggle='maxlength']").each(function(idx, obj) {
            const defaultOptions = {
                warningClass: "badge badge-success",
                limitReachedClass: "badge badge-danger",
                message: '%charsRemaining% remaining',
                placement: 'bottom',
            };

            const $el: any = $(obj);
            const objOptions = $.extend({}, defaultOptions, $el.data());
            $el.maxlength(objOptions);
        });

        $("[data-toggle='timepicker']").each(function(idx, obj) {
            const defaultTimePickerOptions = {
                "showSeconds": true,
                "icons": {
                    "up": "mdi mdi-chevron-up",
                    "down": "mdi mdi-chevron-down"
                }
            };
            const $el: any = $(obj);
            var objOptions = $.extend({}, defaultTimePickerOptions, $el.data());
            $el.timepicker(objOptions);
        });

        $("[data-toggle='datatable']").each(function(idx, obj) {
            const $el: any = $(obj);
            if (!($.fn as any).dataTable.isDataTable($el)) {
                const objOptions = $el.data();
                const sortColIndex = objOptions.sortColIndex;
                const sortColDir = objOptions.sortColDir;
                const order = sortColIndex || sortColDir ? [[ sortColIndex, sortColDir ]] : [];
                $el.DataTable({
                    stateSave: false,
                    // letters in the DOM string have meanings according to: https://datatables.net/reference/option/dom
                    // paging and fitlers and table information are turned off
                    dom: "rt",
                    order: order,
                });
            }
        });
    });

    $(document).on("turbolinks:before-render", function() {
        if (typeof ($('.side-nav') as any).metisMenu === "function") {
            ($('.side-nav') as any).metisMenu('dispose');
        }
    
        const $sideBar = $('#left-side-menu-container');
        $sideBar.find(".mm-active").removeClass("mm-active");
        $sideBar.find(".mm-show").removeClass("mm-show");
        $sideBar.find(".mm-collapse").removeClass("mm-collapse");
        $sideBar.find(".active").removeClass("active");
        $sideBar.find("ul").each(function() {
            $(this).css({ height: "initial" });
        });

        $("[data-plugin='range-slider']").each(function() {
            const $el: any = $(this);
            const rangeSlider = $el.data("ionRangeSlider");
            if (rangeSlider) {
                rangeSlider.destroy();
            }
        });

        $("[data-toggle='select2']").each(function() {
            const $el: any = $(this);
            if ($el.hasClass("select2-hidden-accessible")) {
                $el.select2('destroy');
            }
        });

        $("[data-toggle='datatable']").each(function(idx, obj) {
            const $el: any = $(obj);
            if (($.fn as any).dataTable.isDataTable($el)) {
                $el.DataTable().destroy();
            }
        });
    });

    // submit GET forms using turbolinks
    $(document).on("click", "form.js-turbolinks-get-form button, form.js-turbolinks-get-form input[type='submit']", function(e) {
        const button = e.currentTarget;
        const form = $(this).parents("form").get(0);
        const formParameters = [...new FormData(form).entries()];

        if ((button instanceof HTMLInputElement || button instanceof HTMLButtonElement)) {
            if (button.dataset["turbolinks"] === "false") {
                // used to disable turbolinks behaviour for file downloads, to prevent turbolinks sending an additional http request, and prevent browser URL from being updated
                return;
            }

            if (button.name && button.value) {
                // add the submit buttons name and value as URL params
                formParameters.push([button.name, button.value]);
            }
        }

        const url = form.action + "?" + formParameters.map(p => `${encodeURIComponent(p[0])}=${encodeURIComponent(p[1].toString())}`).join("&");
        Turbolinks.visit(url)

        e.preventDefault();
    });
}

export enum DataKeys {
    PrefetchSource = "prefetch-source",
    PrefetchVal = "prefetch-val",
    Placeholder = "placeholder",
    LazyLoad = "lazy-load",
    LazyLoadItems = "lazy-load-items",
}

function setupSelect2() {
    $("[data-toggle='select2']").each(function(idx, obj) {
        (async () => {
            const $el = $(obj);
            const options: { data?: Array<OptionItem<string>>, ajax?: { transport?: Function} } = {};
            const prefetchSource = $el.data(DataKeys.PrefetchSource);
            if (prefetchSource) {
                // populate the <select> dynamically using data linked by 'prefetch-source'
                const json = await getPrefetchedData(prefetchSource);
                const defaultValue = tryParseString($el.data(DataKeys.PrefetchVal));
                let allItems: Array<OptionItem<string>> = SelectBoxManager.readOptionsFromDom($el);
                allItems = allItems.concat(json.map(i => ({ id: i.id.toString(), text: i.text })));
                
                const selectedOption = allItems.find(o => typeof defaultValue !== "undefined" && o.id.toString() === defaultValue);
                if (selectedOption) {
                    SelectBoxManager.setSelectedOptionAndRenderIfNotExists($el, selectedOption.id, selectedOption.text, false);
                }

                $el.data(DataKeys.LazyLoadItems, allItems); // set lazy load items up for retrieval when search is used (see LazyLoad logic below)
            }

            if (prefetchSource || $el.data(DataKeys.LazyLoad)) {
                // use select2's custom transport option to search an array of data for options
                const maxItems = 20;
                options.ajax = {
                    transport: (params: any, success: any, failure: any) => {
                        const prefetchItems = $el.data(DataKeys.LazyLoadItems);
                        if (!Array.isArray(prefetchItems)) {
                            throw new Error(`Unexpected value for key '${DataKeys.LazyLoadItems}'. Type: ${typeof prefetchItems}`);
                        }
    
                        let result = paginatedArraySearch(prefetchItems, params?.data?.q, maxItems, params?.data?.page);
                        const promise = new Promise(function(resolve, reject) {
                            // fake promise just to conform to expected call pattern for select2 transport
                            resolve(result);
                        });
                        promise.then(success);
                        promise.catch(failure);
                    }
                };
            }
            (<any>$el).select2(options);
        })();
    });
}

function setupSidebar() {
    const $sideBar = $('#left-side-menu-container');
    if ($sideBar.data("sidebar-initialised") !== true) {
        ($ as any).LeftSidebar.init();
        $sideBar.data("sidebar-initialised", true);
    }
}

type ISelect2TransportResult<T> = { results: Array<OptionItem<T>>, pagination?: { more: true } };

function paginatedArraySearch<T>(options: Array<OptionItem<T>>, searchTerm: string | undefined, maxResults: number, page: number | undefined): ISelect2TransportResult<T> {
    if (typeof page !== "number") {
        page = 1;
    }

    const items: Array<OptionItem<T>> = [];
    const regex = !!searchTerm ? new RegExp(searchTerm, "i") : null;
    const resultsToSkip = maxResults * (page - 1);
    let resultsSkipped = 0;
    let count = 0;
    for (const option of options) {
        if (!searchTerm || regex.test(option.text)) {
            if (resultsSkipped < resultsToSkip) {
                resultsSkipped++;
            } else {
                items.push(option);
                count++;
            }
        }

        if (count >= maxResults + 1) {
            break;
        }
    }
    
    const result: ISelect2TransportResult<T> = { results: items };
    if (count > maxResults) {
        result.pagination = { more: true };
    }

    return result;
}
